import type { MonitoredCamera } from "../../types";
import { INVALID_NOK, searchText } from "../pipeline/helpers";

/** A camera of the board with its derived state: connectivity, whether it is watched, its problems. */

export type ConnectivityKind = "online" | "offline" | "unknown";
export type ProblemSeverity = "critical" | "warning";

export type CameraProblem =
  | { code: "offline"; severity: ProblemSeverity }
  /** noPhoto: how many of them never got their photo (the rest failed in the evaluation) */
  | { code: "stuck"; severity: ProblemSeverity; count: number; noPhoto: number }
  | { code: "uncaptured"; severity: ProblemSeverity; count: number; manual: boolean }
  | { code: "triggerFailures"; severity: ProblemSeverity; count: number; lastFailed: boolean; error: string | null }
  | { code: "uploadRejections"; severity: ProblemSeverity; count: number; lastRefused: boolean; error: string | null }
  | { code: "restartPending"; severity: ProblemSeverity; camera: boolean; service: boolean }
  | { code: "invalidShare"; severity: ProblemSeverity; share: number };

export interface CameraRow {
  camera: MonitoredCamera;
  kind: ConnectivityKind;
  /** assigned to a branch that captures photos: its state matters */
  watched: boolean;
  problems: CameraProblem[];
  severity: ProblemSeverity | null;
  search: string;
}

export type CameraFilter = "all" | "problems" | "assigned" | "unassigned";
export const CAMERA_FILTERS: CameraFilter[] = ["all", "problems", "assigned", "unassigned"];

export const connectivityKind = (camera: MonitoredCamera): ConnectivityKind => {
  const active = camera.connectivity?.active;
  if (active === null || active === undefined) return "unknown";
  return active ? "online" : "offline";
};

export function cameraRow(camera: MonitoredCamera): CameraRow {
  const kind = connectivityKind(camera);
  const watched = camera.assigned && !!camera.branch?.enable_acquisition;
  const stats = camera.stats;
  const activity = camera.activity ?? {};
  const problems: CameraProblem[] = [];

  if (watched && kind === "offline") problems.push({ code: "offline", severity: "critical" });
  if (stats && stats.missing_24h > 0) {
    // a photo that arrived and then failed in the evaluation is not the camera's fault: a warning here
    // (an older backend without the split counts every one as the camera's)
    const noPhoto = stats.missing_no_photo_24h ?? stats.missing_24h;
    problems.push({ code: "stuck", severity: noPhoto > 0 ? "critical" : "warning", count: stats.missing_24h, noPhoto });
  }
  if (stats && stats.uncaptured > 0) {
    // at a branch whose staff fire the camera from the app, an uncaptured pizza is a habit, not a fault
    const manual = camera.branch?.enable_bb_trigger === false;
    problems.push({ code: "uncaptured", severity: manual ? "warning" : "critical", count: stats.uncaptured, manual });
  }
  if ((activity.trigger_failures ?? 0) > 0) {
    const lastFailed = activity.last_trigger_ok === false;
    problems.push({
      code: "triggerFailures",
      severity: lastFailed ? "critical" : "warning",
      count: activity.trigger_failures ?? 0,
      lastFailed,
      error: activity.last_trigger_error ?? null,
    });
  }
  if ((activity.upload_rejections ?? 0) > 0) {
    const lastRefused = activity.last_upload_status !== undefined && activity.last_upload_status !== 200;
    problems.push({
      code: "uploadRejections",
      severity: lastRefused ? "critical" : "warning",
      count: activity.upload_rejections ?? 0,
      lastRefused,
      error: activity.last_upload_error ?? null,
    });
  }
  if (camera.restart_camera || camera.restart_service) {
    problems.push({ code: "restartPending", severity: "warning", camera: camera.restart_camera, service: camera.restart_service });
  }
  const share = stats?.invalid_share_24h;
  if (typeof share === "number" && share >= INVALID_NOK) problems.push({ code: "invalidShare", severity: "warning", share });

  const severity = problems.some((p) => p.severity === "critical")
    ? "critical"
    : problems.length
      ? "warning"
      : null;

  return {
    camera,
    kind,
    watched,
    problems,
    severity,
    search: searchText(camera.name, camera.label, camera.id, camera.branch?.name, camera.branch_id),
  };
}

const severityRank = (severity: ProblemSeverity | null) => (severity === "critical" ? 0 : severity === "warning" ? 1 : 2);

/** problem cameras first (critical before warnings), then watched, assigned, by branch and name */
export function compareRows(a: CameraRow, b: CameraRow): number {
  return (
    severityRank(a.severity) - severityRank(b.severity) ||
    Number(b.watched) - Number(a.watched) ||
    Number(b.camera.assigned) - Number(a.camera.assigned) ||
    (a.camera.branch?.name ?? "").localeCompare(b.camera.branch?.name ?? "") ||
    (a.camera.name ?? "").localeCompare(b.camera.name ?? "")
  );
}

export function matchesFilter(row: CameraRow, filter: CameraFilter): boolean {
  if (filter === "problems") return row.problems.length > 0;
  if (filter === "assigned") return row.camera.assigned;
  if (filter === "unassigned") return !row.camera.assigned;
  return true;
}
